import { useState, useEffect } from 'react';
import { routesService } from '../../_services/routesService';

export const useRoutes = (slug, isEditable = false) => {
  const [routes, setRoutes] = useState([]);
  const [isEditingRoutes, setIsEditingRoutes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Cargar rutas al montar o cambiar el slug
   */
  useEffect(() => {
    const fetchRoutes = async () => {
      if (!slug) return;

      setLoading(true);
      setError(null);

      try {
        const data = await routesService.getRoutes(slug);
        setRoutes(data);
      } catch (error) {
        console.error('Error al cargar las rutas:', error);
        setError('Error al cargar las rutas. Por favor, recarga la página.');
      } finally {
        setLoading(false);
      }
    };

    fetchRoutes();
  }, [slug]);

  /**
   * Manejar cambio en cualquier campo de la ruta (incluido map_data)
   */
  const handleRouteChange = (index, field, value) => {
    const newRoutes = [...routes];
    newRoutes[index][field] = value;
    setRoutes(newRoutes);
    
    // Limpiar error global si el usuario empieza a corregir
    if (error) setError(null);
  };

  /**
   * Manejar cambio de imagen
   */
  const handleRouteImageChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      const newRoutes = [...routes];
      newRoutes[index].image = URL.createObjectURL(file);
      newRoutes[index].file = file;
      setRoutes(newRoutes);
    }
  };

  /**
   * Agregar ruta nueva
   */
  const handleAddRoute = () => {
    if (routes.length < routesService.MAX_ROUTES) {
      setRoutes([...routes, routesService.createEmptyRoute(routes.length)]);
    } else {
      alert(`Solo puedes agregar hasta ${routesService.MAX_ROUTES} rutas.`);
    }
  };

  /**
   * Eliminar ruta
   */
  const handleDeleteRoute = (indexToDelete) => {
    const newRoutes = routes.filter((_, index) => index !== indexToDelete);
    setRoutes(newRoutes);
  };

  /**
   * Guardar rutas (Aquí ocurre la magia de la validación)
   */
  const handleRoutesSave = async () => {
    setIsSaving(true);
    setError(null);

    try {
      // 1. Filtrar rutas vacías
      const filteredRoutes = routes.filter(route => 
        route.description && route.description.trim() !== ""
      );

      if (filteredRoutes.length === 0) {
        throw new Error("Debes agregar al menos una ruta con descripción antes de guardar.");
      }

      // 2. Llamar al servicio (Aquí routesService.validateMapJson hará su trabajo)
      // Si el JSON está mal, routesService lanzará un Error con el mensaje detallado (Línea, Columna, etc.)
      await routesService.saveRoutes(slug, routes);
      
      // 3. Si llega aquí, todo fue válido y guardado con éxito
      alert('✅ Rutas guardadas correctamente.');
      setIsEditingRoutes(false);
      
      // 4. Recargar las rutas limpias desde el backend
      const updatedRoutes = await routesService.getRoutes(slug);
      setRoutes(updatedRoutes);
      
      return true;
    } catch (error) {
      console.error('Error al guardar rutas:', error);
      
      // 🚨 AQUÍ SE MUESTRA LA ALERTA INTELIGENTE QUE VIENE DEL SERVICE
      const errorMessage = error.message || 'Hubo un error inesperado al guardar. Intenta de nuevo.';
      setError(errorMessage);
      alert(`⚠️ Error al guardar:\n\n${errorMessage}`);
      
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Alternar modo edición
   */
  const toggleEditing = () => {
    setIsEditingRoutes(!isEditingRoutes);
    setError(null); // Limpiar errores al cambiar de modo
  };

  /**
   * Cancelar edición y restaurar datos originales
   */
  const cancelEdit = async () => {
    try {
      setLoading(true);
      const data = await routesService.getRoutes(slug);
      setRoutes(data);
      setIsEditingRoutes(false);
      setError(null);
    } catch (error) {
      console.error('Error al recargar rutas:', error);
      setError('Error al recargar las rutas originales.');
    } finally {
      setLoading(false);
    }
  };

  return {
    // Estado
    routes,
    isEditingRoutes,
    isSaving,
    loading,
    error,

    // Setters (por si los necesitas en el componente)
    setRoutes,
    setIsEditingRoutes,

    // Handlers
    handleRouteChange,
    handleRouteImageChange,
    handleAddRoute,
    handleDeleteRoute,
    handleRoutesSave,
    toggleEditing,
    cancelEdit,
  };
};